import { Component, Input } from "@angular/core";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { AuthResponse, PonderProductType, RenewSubscriptionRequest, Subscription, SubscriptionStatus } from "@apiserver-rest/ponder-auth-client-base";
import { NavigationService } from "@ponder-core/navigation/navigation.service";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { SvgIconComponent } from "@ponder-shared/components/svg-icon.component";
import { FormatDatePipe } from "@ponder-shared/pipes/format-date.pipe";
import { SvgIcon } from "@ponder-shared/utils/svgIcons";
import { take } from "rxjs";

@Component({
    selector: "user-subscription-row",
    imports: [BusySpinComponent, FormatDatePipe, SvgIconComponent],
    templateUrl: "./user-subscription-row.component.html"
})
export class UserSubscriptionRowComponent {
    @Input({ required: true }) public subscription!: Subscription;

    public showMoreIcon: SvgIcon = SvgIcon.Information;

    public productName!: string;
    public statusBadgeColor!: string;
    public statusBadgeLabel!: string;
    public statusBadgeDescription!: string;

    public canCancel: boolean = false;
    public isConfirmingCancellation: boolean = false;
    public isCancelling: boolean = false;

    public canRenew: boolean = false;
    public isRenewing: boolean = false;

    public actionError: boolean = false;
    public showMore: boolean = false;

    public constructor(private ponderAuthClient: PonderAuthClient, private navigation: NavigationService)
    {

    }

    public ngOnInit(): void {
        this.setProductName();
        this.setStatusBadge();
    }

    private setProductName(): void {
        switch (this.subscription.product) {
            case PonderProductType.UserPlusMonthlyMembership:
                this.productName = "User+ (Monthly)";
                break;
                
            default:
                this.productName = "Unknown";
                break;
        }
    }

    private setStatusBadge(): void {
        this.statusBadgeLabel = (this.subscription.status ?? SubscriptionStatus.Inactive) as string;

        switch (this.statusBadgeLabel) {
            case SubscriptionStatus.Active:
                this.statusBadgeColor = "#15803d";
                this.statusBadgeDescription = "Subscription benefits are active and will be renewed at the next billing cycle.";
                this.canCancel = true;
                break;

            case SubscriptionStatus.Cancelled:
                this.statusBadgeColor = "#c2410c";
                this.statusBadgeDescription = "Subscription benefits are active, but will not be renewed at the next billing cycle.";
                this.canRenew = true;
                break;

            case SubscriptionStatus.Pending:
                this.statusBadgeColor = "#0369a1";
                this.statusBadgeDescription = "Subscription is being setup and is not yet active.";
                this.canCancel = true;
                break;

            case SubscriptionStatus.Inactive:
            default:
                this.statusBadgeColor = "#334155";
                this.statusBadgeDescription = "This subscription is no longer active and can not be renewed. A new subscription must be made.";
                break;
        }
    }

    public onRenewClicked(): void {
        if (!this.canRenew || this.isRenewing) {
            return;
        }
        
        this.isRenewing = true;
        this.actionError = false;

        const request = new RenewSubscriptionRequest({
            subscriptionId: this.subscription.id
        });

        this.ponderAuthClient
            .subscriptionRenew(request)
            .pipe(take(1))
            .subscribe({
                next: (response: AuthResponse) => {
                    if (response.success) {
                        this.navigation.reload();
                    } else {
                        this.actionError = true;
                    }
                },
                error: () => {
                    this.actionError = true;
                }
            }).add(() => this.isRenewing = false);
    }

    public onCancelClicked(): void {
        if (!this.canCancel || this.isCancelling) {
            return;
        }

        this.isConfirmingCancellation = true;
        this.actionError = false;
    }

    public onNevermindCancelClicked(): void {
        if (this.isCancelling) {
            return;
        }

        this.isConfirmingCancellation = false;
    }

    public onConfirmCancelClicked(): void {
        if (this.isCancelling) {
            return;
        }

        this.isConfirmingCancellation = false;
        this.isCancelling = true;
        
        const cancelImmediately: boolean = (this.subscription.status == SubscriptionStatus.Pending);
        
        this.ponderAuthClient
            .subscriptionCancel(this.subscription.id, cancelImmediately)
            .pipe(take(1))
            .subscribe({
                next: (response: AuthResponse) => {
                    if (response.success) {
                        this.navigation.reload();
                    } else {
                        this.actionError = true;
                    }
                },
                error: () => {
                    this.actionError = true;
                }
            })
            .add(() => this.isCancelling = false);
    }
}